
export class EarthServer {

    /**
     * The available state variables and their meanings are documented in sharedStore.js and sessionStore.js.
     *
     * @return {Promise<object>} all current values of the globe's state variables
     */
    getState() {}

    /**
     * Modify the state variables of the globe.
     *
     * The available state variables and their meanings are documented in sharedStore.js and sessionStore.js.
     *
     * Example:
     *
     *     saveState({hd_enabled: true, overlay_type: "temp"});  // enable HD mode and switch to the temperature overlay
     *
     * @see EarthServer.getState
     * @see EarthClient.stateChanged
     *
     * @param {object} changes {k:v} pairs
     * @param {*?} meta optional metadata that is passed along to all state change listeners
     */
    saveState(changes, meta) {}

    /**
     * Returns the D3 projection in current use. The projection is provided as an object:
     *
     *     {
     *         name:       string    // example: "orthographic"
     *         clipAngle:  number
     *         clipExtent: number[]
     *         scale:      number
     *         translate:  number[]
     *         center:     number[]
     *         angle:      number
     *         reflectX:   boolean
     *         reflectY:   boolean
     *         rotate:     number[]
     *         precision:  number
     *     }
     *
     * Every field is optional and so may be missing, except "name", which is used to identify the projection. These
     * fields correspond exactly with the equivalently named D3 projection methods (except for "name") and can be
     * used to construct an actual D3 projection instance.
     *
     * @see {@link https://github.com/d3/d3-geo#projections}
     *
     * @return {Promise<object>} the current projection, or undefined while the app is initializing
     */
    getProjection() {}

    /**
     * Change the orientation of the globe.
     *
     *     scale:       set the scale to a specific value, or string "default" to use the default scale
     *     scaleBy:     multiply the current scale by this amount
     *     rotate:      [λ, φ] set the three-axis rotation angles, in degrees, or "default" to use the default rotation
     *     rotateBy:    [Δλ, Δφ] adjust the three-axis rotation angles by these amounts, in degrees
     *     translate:   [x, y] set the projection's center in pixel coordinates, or "default" for viewport center
     *     translateBy: [Δx, Δy] move the projection's center by these amounts, in pixel coordinates
     *
     * If scale and scaleBy are both provided, the scale is first applied and then scaleBy is applied on that value.
     * Same for rotate and rotateBy.
     *
     * Example:
     *
     *     reorient({scaleBy: 1.25, rotate: [-139.7, -35.7]});  // increase zoom by 25% and rotate to Tokyo coords
     *
     * @see {@link https://github.com/d3/d3-geo#projection_scale}
     * @see {@link https://github.com/d3/d3-geo#projection_rotate}
     * @see {@link https://bl.ocks.org/mbostock/4282586}
     *
     * @param {{scale: number|string, scaleBy: number,
     *         rotate: number[]|string, rotateBy: number[],
     *         translate: number[]|string, translateBy: number[]}} operation
     */
    reorient(operation) {}

    /**
     * Returns the globe's current set of layers in display order as an array of objects with the following schema:
     *
     *     {
     *         type: string,     // one of "animation", "overlay", or "annotation"
     *         product: Product  // see Product section in schemas.txt
     *     }
     *
     * Note: a layer may not be enabled, in which case the slot in the array for that layer will be undefined.
     *
     * Example:
     *
     *   [
     *     {type: "animation", product: ...},
     *     {
     *         type: "overlay",
     *         product: {
     *             name: "temp",
     *             source: "GFS / NCEP / US National Weather Service",
     *             description: "Temperature @ 850hPa",
     *             validTime: "2021-08-29 07:00Z",
     *             ...
     *         }
     *     },
     *     {type: "annotation", product: ...},
     *   ]
     *
     * @return {Promise<array>} the globe's current layers
     */
    getLayers() {}

    /**
     * Samples each layer at either the specified screen position or the lon,lat coordinates. The data samples are
     * returned in an array that corresponds one to one with the results of getLayers().
     *
     *     [
     *       DataSample,  // sample for layer 0 (see schemas.txt)
     *       DataSample,  // sample for layer 1
     *       ...
     *     ]
     *
     * Note: a layer may not be enabled or the layer may have no sample for the location, in which case the slot in the
     * array for that layer will be undefined.
     *
     * Example:
     *
     *     [
     *         [
     *             {value: [2.5, 3.3], units: "m/s"},
     *             {value: [4.5, 5.7], units: "km/h"},
     *         ],
     *         [
     *             {value: 18.0, units: "°C"},
     *             {value: 64.4, units: "°F"},
     *         ],
     *         ...
     *     ]
     *
     * @param {number[2]} point [x, y] screen position (can be null or undefined if coordinates are supplied)
     * @param {number[2]?} coordinates [lon, lat] coordinates on the globe (takes precedence over point if specified)
     * @return {Promise<array>} the data samples for the specified location
     */
    sampleAt(point, coordinates) {}

    /**
     * Adds a GeoJSON Feature {@link https://datatracker.ietf.org/doc/html/rfc7946#section-3.2} to the globe's
     * annotation layer. If an annotation with the associated id does not exist, it is appended to the list of
     * annotations. If the annotation already exists, it is replaced in place. The annotation is deleted if the
     * provided feature is null.
     *
     * The annotation is rendered according to the styling rules contained in the feature's "properties" section:
     *
     *     {
     *         "type": "Feature",
     *         "geometry": {"type": "Point", "coordinates": [0, 0]},
     *         "properties": {
     *
     *             "fill": D3_color_specifier    // no fill occurs when undefined
     *             "stroke": D3_color_specifier  // no stroke is drawn when undefined
     *             "strokeWidth": number         // width of the stroke in pixels, default: 1
     *             "strokeDasharray": number[]   // length of alternating dashes and gaps, optional
     *             "pointRadius": number         // for Point geometries, the circle radius in pixels, default: 5
     *
     *         }
     *     }
     *
     * Note: if both fill and stroke are unspecified, then the default SVG styling will be used.
     *
     * D3_color_specifier is a specifier string accepted by d3.color(): {@link https://github.com/d3/d3-color#color}
     *
     * @see {@link https://datatracker.ietf.org/doc/html/rfc7946}
     *
     * @param {string} id unique id for this annotation
     * @param {object|null} feature a GeoJSON Feature object to render using the styling rules above, or null to remove
     */
    annotate(id, feature) {}

}

export class EarthClient {

    /**
     * Invoked when any of the globe's state variables change.
     *
     * The available state variables and their meanings are documented in sharedStore.js and sessionStore.js.
     *
     * Example:
     *
     *     stateChanged({hd_enabled: true}, {hd_enabled: false});  // hd_enabled changed from false to true
     *
     * @see EarthServer.getState
     * @see EarthServer.saveState
     *
     * @param {object} delta {k:v} pairs that changed
     * @param {object} old {k:v} pairs of the previous values
     * @param {*} meta metadata passed in during the save operation, if any
     */
    stateChanged(delta, old, meta) {}

    /**
     * Invoked when any of the globe's layers change. All layers are provided each time this method is invoked and
     * have exactly the same schema as {@link EarthServer.getLayers}.
     *
     * @see EarthServer.getLayers
     *
     * @param {object[]} layers the globe's current layers
     */
    layersChanged(layers) {}

    /**
     * Invoked when the user clicks the globe.
     *
     * Example:
     *
     *     click([150, 273], [139.7, 35.7]);  // click occurred at x=150,y=273, corresponding to lon=139.7, lat=35.7
     *
     * @param {number[2]} point [x, y] screen position of the click
     * @param {number[2]} coordinates [lon, lat] coordinates of the click
     */
    click(point, coordinates) {}  // UNDONE: onClick? clicked? match tense of "stateChanged"?

    /**
     * Invoked when the globe is starting a reorientation operation, i.e., the globe's projection is changing due to,
     * for example, user interaction.
     *
     * Each reorientation operation has the following invocation pattern:
     *
     *     reorientStart
     *     (reorientStep)*
     *     reorientEnd
     *
     * There will be zero or more "steps" to the operation, when the projection is in an intermediate state. The
     * number of steps depends on how long the operation continues (for example, how long the user keeps dragging
     * the globe). When the operation is complete, reorientEnd is called with the final state of the projection.
     *
     * @see reorientStep
     * @see reorientEnd
     * @see EarthServer.getProjection
     *
     * @param {array} projection
     */
    reorientStart(projection) {}

    /**
     * Invoked while the globe is undergoing a reorientation operation.
     *
     * @see reorientStart
     * @see EarthServer.getProjection
     *
     * @param {array} projection
     */
    reorientStep(projection) {}

    /**
     * Invoked when the globe has finished a reorientation operation.
     *
     * @see reorientStart
     * @see EarthServer.getProjection
     *
     * @param {array} projection
     */
    reorientEnd(projection) {}

}
